/**
 * 离开页面预警服务
 * 采用单例模式，职责单一，易于测试和维护
 */
import { getDevConf } from '@/api/basic/displaySetting';
import { getCookie } from '@/utils/cookie';
import Vue from 'vue';
import LeavePageWarningDialog from '../components/leave-page-warning-dialog.vue';

interface TaskInfo {
  is_start_with_pickup?: number;
  wave_type?: number;
  order_left?: number;
}

class LeavePageWarningService {
  private static instance: LeavePageWarningService;

  // 功能开关状态
  private enabled = false;

  // 是否已确认离开（用于放行）
  private confirmedLeaving = false;

  // beforeunload 处理器引用
  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => any) | null = null;

  // 弹窗组件实例
  private dialogInstance: any = null;

  // 当前任务信息引用（用于 beforeunload 判断）
  private currentTaskInfo: TaskInfo | null = null;

  private constructor() {
    // 私有构造函数，确保单例
  }

  /**
   * 获取服务实例（单例模式）
   */
  static getInstance(): LeavePageWarningService {
    if (!LeavePageWarningService.instance) {
      LeavePageWarningService.instance = new LeavePageWarningService();
    }
    return LeavePageWarningService.instance;
  }

  /**
   * 初始化服务
   */
  async init(): Promise<void> {
    await this.checkSwitch();
    this.installBeforeUnloadListener();
  }

  /**
   * 销毁服务
   */
  destroy(): void {
    this.uninstallBeforeUnloadListener();
    this.confirmedLeaving = false;
    this.currentTaskInfo = null;
  }

  /**
   * 更新当前任务信息（用于 beforeunload 判断）
   */
  updateTaskInfo(taskInfo: TaskInfo | null): void {
    this.currentTaskInfo = taskInfo;
  }

  /**
   * 重置确认状态（用于取消离开后）
   */
  resetConfirmation(): void {
    this.confirmedLeaving = false;
  }

  /**
   * 检查功能开关
   */
  private async checkSwitch(): Promise<void> {
    try {
      const whs = getCookie('setting_whs_v2') || '';
      const { data } = await getDevConf({
        conf_key: 'checker_leave_page_warning_popup',
        whs_id: whs,
      });
      this.enabled = data?.conf_value === '1';
    } catch (error) {
      console.error('[LeavePageWarning] Failed to check switch:', error);
      this.enabled = false;
    }
  }

  /**
   * 判断是否需要显示预警
   */
  shouldShowWarning(taskInfo: TaskInfo | null | undefined): boolean {
    // 1. 功能开关未开启
    if (!this.enabled) {
      return false;
    }

    // 2. 已经确认离开
    if (this.confirmedLeaving) {
      return false;
    }

    // 3. 没有任务信息
    if (!taskInfo) {
      return false;
    }

    // 4. 不是以 picking 维度进入
    if (taskInfo.is_start_with_pickup !== 1 && taskInfo.wave_type !== 0) {
      return false;
    }

    // 5. Order Left < 1
    if (!taskInfo.order_left || taskInfo.order_left < 1) {
      return false;
    }

    return true;
  }

  /**
   * 安装 beforeunload 监听器
   */
  private installBeforeUnloadListener(): void {
    if (typeof window === 'undefined') return;

    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      // 根据 taskInfo 数据判断是否需要显示预警
      if (!this.shouldShowWarning(this.currentTaskInfo)) {
        return undefined;
      }

      // 标准方式阻止默认行为
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  /**
   * 卸载 beforeunload 监听器
   */
  private uninstallBeforeUnloadListener(): void {
    if (typeof window === 'undefined' || !this.beforeUnloadHandler) return;

    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.beforeUnloadHandler = null;
  }

  /**
   * 显示自定义确认弹窗（用于路由跳转）
   * 返回 Promise<boolean>，true 表示用户确认离开
   */
  async showConfirmDialog(orderLeft: number): Promise<boolean> {
    return new Promise((resolve) => {
      // 创建弹窗组件实例
      const DialogConstructor = Vue.extend(LeavePageWarningDialog);

      this.dialogInstance = new DialogConstructor({
        propsData: {
          orderLeft: orderLeft,
        },
      });

      // 挂载到 body
      const container = document.createElement('div');
      document.body.appendChild(container);
      this.dialogInstance.$mount(container);

      // 显示弹窗并等待用户选择
      this.dialogInstance
        .show()
        .then((confirmed: boolean) => {
          if (confirmed) {
            this.confirmedLeaving = true;
          }
          resolve(confirmed);
        })
        .finally(() => {
          // 清理
          this.cleanupDialog();
        });
    });
  }

  /**
   * 清理弹窗实例
   */
  private cleanupDialog(): void {
    if (this.dialogInstance) {
      this.dialogInstance.$destroy();
      if (this.dialogInstance.$el && this.dialogInstance.$el.parentNode) {
        this.dialogInstance.$el.parentNode.removeChild(this.dialogInstance.$el);
      }
      this.dialogInstance = null;
    }
  }

  /**
   * 处理路由跳转前的检查
   */
  async handleRouteLeave(
    taskInfo: TaskInfo | null | undefined
  ): Promise<boolean> {
    if (!this.shouldShowWarning(taskInfo)) {
      return true; // 允许离开
    }

    const orderLeft = taskInfo?.order_left ?? 0;
    return await this.showConfirmDialog(orderLeft);
  }

  /**
   * 检查是否可以离开（使用内部维护的 taskInfo）
   * 适用于非路由跳转场景（如登出按钮、侧边栏菜单点击等）
   * @returns Promise<boolean> true 表示可以离开
   */
  async checkBeforeLeave(): Promise<boolean> {
    return this.handleRouteLeave(this.currentTaskInfo);
  }
}

// 导出单例实例
export default LeavePageWarningService.getInstance();
